import type { Post } from '../contexts/PostsContext';

const DB_NAME = 'feed-cache';
const DB_VERSION = 1;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export interface FeedCursor {
  timestamp: number;
  postId: string;
}

export interface FeedResponse {
  posts: Post[];
  nextCursor: FeedCursor | null;
  hasMore: boolean;
  users?: Record<string, any>;
  tags?: Record<string, any>;
}

interface CachedBatch {
  cursor: string;
  data: FeedResponse;
  timestamp: number;
}

/**
 * IndexedDB cache for feed data
 * Provides offline support and faster loads
 */
class FeedCache {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize IndexedDB
   */
  async init(): Promise<void> {
    if (this.db) {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      // Check if IndexedDB is supported
      if (!window.indexedDB) {
        console.warn('IndexedDB not supported, caching disabled');
        resolve();
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Store for posts
        if (!db.objectStoreNames.contains('posts')) {
          db.createObjectStore('posts', { keyPath: 'id' });
        }

        // Store for users
        if (!db.objectStoreNames.contains('users')) {
          db.createObjectStore('users', { keyPath: 'id' });
        }

        // Store for feed batches
        if (!db.objectStoreNames.contains('feedBatches')) {
          const store = db.createObjectStore('feedBatches', {
            keyPath: 'cursor',
          });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Cache a feed batch
   */
  async cacheFeedBatch(
    batch: FeedResponse,
    cursor: FeedCursor | null
  ): Promise<void> {
    if (!this.db) {
      await this.init();
      if (!this.db) return; // IndexedDB not supported
    }

    try {
      const transaction = this.db.transaction(
        ['feedBatches', 'posts', 'users'],
        'readwrite'
      );

      // Store batch
      if (cursor) {
        transaction.objectStore('feedBatches').put({
          cursor: JSON.stringify(cursor),
          data: batch,
          timestamp: Date.now(),
        });
      }

      // Store individual posts
      batch.posts.forEach(post => {
        transaction.objectStore('posts').put(post);
      });

      // Store users
      if (batch.users) {
        Object.values(batch.users).forEach(user => {
          transaction.objectStore('users').put(user);
        });
      }

      return new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });
    } catch (error) {
      console.error('Error caching feed batch:', error);
    }
  }

  /**
   * Get cached batch by cursor
   */
  async getCachedBatch(
    cursor: FeedCursor | null
  ): Promise<FeedResponse | null> {
    if (!this.db) {
      await this.init();
      if (!this.db) return null; // IndexedDB not supported
    }

    if (!cursor) return null;

    try {
      const transaction = this.db.transaction(['feedBatches'], 'readonly');
      const store = transaction.objectStore('feedBatches');
      const request = store.get(JSON.stringify(cursor));

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const result = request.result as CachedBatch | undefined;
          if (result && Date.now() - result.timestamp < CACHE_TTL) {
            resolve(result.data);
          } else {
            // Cache expired, remove it
            if (result) {
              store.delete(JSON.stringify(cursor));
            }
            resolve(null);
          }
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error getting cached batch:', error);
      return null;
    }
  }

  /**
   * Get cached post by ID
   */
  async getCachedPost(postId: string): Promise<Post | null> {
    if (!this.db) {
      await this.init();
      if (!this.db) return null;
    }

    try {
      const transaction = this.db.transaction(['posts'], 'readonly');
      const store = transaction.objectStore('posts');
      const request = store.get(postId);

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          resolve(request.result || null);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error getting cached post:', error);
      return null;
    }
  }

  /**
   * Clear expired cache entries
   */
  async clearExpired(): Promise<void> {
    if (!this.db) {
      await this.init();
      if (!this.db) return;
    }

    try {
      const transaction = this.db.transaction(['feedBatches'], 'readwrite');
      const store = transaction.objectStore('feedBatches');
      const index = store.index('timestamp');
      const request = index.openCursor();

      request.onsuccess = event => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const batch = cursor.value as CachedBatch;
          if (Date.now() - batch.timestamp > CACHE_TTL) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
    } catch (error) {
      console.error('Error clearing expired cache:', error);
    }
  }

  /**
   * Clear all cache
   */
  async clear(): Promise<void> {
    if (!this.db) {
      await this.init();
      if (!this.db) return;
    }

    try {
      const transaction = this.db.transaction(
        ['feedBatches', 'posts', 'users'],
        'readwrite'
      );

      transaction.objectStore('feedBatches').clear();
      transaction.objectStore('posts').clear();
      transaction.objectStore('users').clear();

      return new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }
}

export const feedCache = new FeedCache();

// Clear expired cache on init
if (typeof window !== 'undefined') {
  feedCache.init().then(() => {
    feedCache.clearExpired();
  });
}
